import { type AriaRole, type FC, memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@remix-run/react';
import classNames from 'classnames';

import { Tooltip } from '~/components/Tooltip';

interface SidebarNavItemProps {
  icon: ReactNode;
  label: string;
  tooltip?: string;
  isActive?: boolean;
  /** Emphasizes an inactive item that holds activity the viewer hasn't seen. */
  isUnread?: boolean;
  trailingNode?: ReactNode;
  /** When greater than zero, shows a compact unread count badge. */
  badgeCount?: number;
  to?: string;
  onClick?: () => void;
  role?: AriaRole;
}

const SidebarNavItemBase: FC<SidebarNavItemProps> = ({
  icon,
  label,
  tooltip,
  isActive = false,
  isUnread = false,
  trailingNode,
  badgeCount,
  to,
  onClick,
  role,
}) => {
  const { t } = useTranslation();
  const isEmphasized = isUnread && !isActive;
  const className = classNames(
    'flex items-center gap-2 h-10 px-3 py-2 rounded-full w-full text-left transition-colors',
    {
      'bg-community-text-on-bg/[0.16] text-community-text-on-bg': isActive,
      'hover:bg-community-text-on-bg/[0.08]': !isActive,
      'text-community-text-on-bg': isEmphasized,
      'text-community-text-on-bg/80': !isActive && !isEmphasized,
    },
  );

  const content = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-full">{icon}</span>
      {/* The mobile drawer reuses this sidebar below md; 13px design restored at md+. */}
      <span
        className={classNames('flex-1 text-[15px] leading-5 tracking-[-0.13px] md:text-[13px] md:leading-4', {
          'font-semibold': isEmphasized,
          'font-medium': !isEmphasized,
        })}
      >
        {label}
      </span>
      {isEmphasized && <span className="sr-only">{t('community.space.unread')}</span>}
      {typeof badgeCount === 'number' && badgeCount > 0 ? (
        <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-community-accent px-1 text-[10px] font-semibold leading-none text-community-text-on-primary">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : null}
      {trailingNode}
    </>
  );

  const element = to ? (
    <Link to={to} className={className} onClick={onClick} role={role} aria-current={isActive ? 'page' : undefined}>
      {content}
    </Link>
  ) : (
    <button type="button" className={className} onClick={onClick} role={role}>
      {content}
    </button>
  );

  if (!tooltip) {
    return element;
  }

  return (
    <Tooltip content={<span className="block max-w-[240px]">{tooltip}</span>} placement="right">
      {element}
    </Tooltip>
  );
};

export const SidebarNavItem = memo(SidebarNavItemBase);
